import type JoltNamespace from 'jolt-physics/wasm-compat';
import * as three from 'three';

/**
 * "Snap" is a quick ThreeJS -> Jolt interface
 * for simple physics simulation.
 */
export namespace Snap {
  export class Engine {
    static globalNamespace: null | typeof JoltNamespace = null;
    static globalModule: null | (() => Promise<typeof JoltNamespace>) = null;
    static globalEngine: null | Engine;
    
    static async Load() {
      if (!this.globalModule)
        this.globalModule = (await import('jolt-physics/wasm-compat')).default

      if (!this.globalNamespace)
        this.globalNamespace = await this.globalModule();

      return new Engine(this.globalNamespace);
    }

    namespace: typeof JoltNamespace;
    get Jolt() {
      return this.namespace;
    }

    constructor(Jolt: typeof JoltNamespace) {
      this.namespace = Jolt;
    }

    createSimulation() {
      return new Simulation(this);
    }
  }

  export class Simulation {
    interface: JoltNamespace.JoltInterface;
    namespace: typeof JoltNamespace;

    bodyInterface: JoltNamespace.BodyInterface;
    physicsSystem: JoltNamespace.PhysicsSystem;

    engine: Engine;

    bodies: Body[] = [];
    objectLayer: number;

    disposed = false;

    constructor(engine: Engine) {
      this.engine = engine;
      this.namespace = engine.namespace;
      const Jolt = this.namespace;

      // Create very simple object layer filter with only a single layer
      const MAIN_LAYER = 0;
      let diceObjectLayerFilter = new Jolt.ObjectLayerPairFilterTable(1);
      // Can collide with itself
      diceObjectLayerFilter.EnableCollision(MAIN_LAYER, MAIN_LAYER);
      this.objectLayer = MAIN_LAYER;


      const broadPhaseLayer  = new Jolt.BroadPhaseLayer(0);
      let broadPhaseLayerInterface = new Jolt.BroadPhaseLayerInterfaceTable(1, 1);

      broadPhaseLayerInterface.MapObjectToBroadPhaseLayer(MAIN_LAYER, broadPhaseLayer);
      Jolt.destroy(broadPhaseLayer); // 'BP_LAYER' has been copied into bpInterface

      let broadPhaseLayerFilter = new Jolt.ObjectVsBroadPhaseLayerFilterTable(broadPhaseLayerInterface, 1, diceObjectLayerFilter, 1);

      // Create a jolt interface
      let settings = new Jolt.JoltSettings();
      settings.mObjectLayerPairFilter = diceObjectLayerFilter;
      settings.mBroadPhaseLayerInterface = broadPhaseLayerInterface;
      settings.mObjectVsBroadPhaseLayerFilter = broadPhaseLayerFilter;

      let joltInterface = new Jolt.JoltInterface(settings); // Everything in 'settings' has now been copied into 'jolt', the 3 interfaces above are now owned by 'jolt'
      Jolt.destroy(settings);

      this.interface = joltInterface;

      // and the floor
      let physicsSystem = this.interface.GetPhysicsSystem();
      let bodyInterface = physicsSystem.GetBodyInterface();

      this.bodyInterface = bodyInterface;
      this.physicsSystem = physicsSystem;

      let material = new Jolt.PhysicsMaterial();
      let normal = new Jolt.Vec3(0, 1, 0);
      let plane = new Jolt.Plane(normal, 0);
      let planeShapeSettings = new Jolt.PlaneShapeSettings(plane, material); // 'material' is now owned by 'box'


      let shapeResult = planeShapeSettings.Create();
      let planeShape = shapeResult.Get();
      shapeResult.Clear(); // We no longer need the shape result, it keeps a reference to 'shape' (which it will also release the next time you create another shape)
      planeShape.AddRef(); // We want to own this shape so we can delete 'compound' which internally keeps a reference

      Jolt.destroy(normal);
      Jolt.destroy(plane);
      Jolt.destroy(planeShapeSettings);

      let bodyPosition = new Jolt.RVec3(0, 0, 0);
      let bodyRotation = new Jolt.Quat(0, 0, 0, 1);

      let creationSettings = new Jolt.BodyCreationSettings(planeShape,
        bodyPosition,
        bodyRotation,
        Jolt.EMotionType_Static,
        this.objectLayer
      ); // 'creationSettings' now holds a reference to 'shape'

      // Jolt.destroy(bodyPosition);
      // Jolt.destroy(bodyRotation);
      
      planeShape.Release(); // We no longer need our own reference to 'shape' because 'creationSettings' now has one

      const planeBody = bodyInterface.CreateBody(creationSettings);
      Jolt.destroy(creationSettings); // 'creationSettings' no longer needed, all settings and the shape reference went to 'body'

      bodyInterface.AddBody(planeBody.GetID(), this.namespace.EActivation_Activate)
    }

    /**
     * This instance runs at 30fps
     */
    tick() {
      this.interface.Step(1.0 / 30.0, 1);
    }

    dispose() {
      let physicsSystem = this.interface.GetPhysicsSystem();
      let bodyInterface = physicsSystem.GetBodyInterface();

      for (const body of this.bodies) {
        // Should use the bulk functions
        bodyInterface.RemoveBody(body.jBody.GetID());
        bodyInterface.DestroyBody(body.jBody.GetID()); // This will internally do the equivalent of Jolt.destroy(body)
      }

      // Clean up the jolt interface
      this.namespace.destroy(this.interface);
      this.disposed = true;
    }

    add(body: Body) {
      let physicsSystem = this.interface.GetPhysicsSystem();
      let bodyInterface = physicsSystem.GetBodyInterface();

      this.bodies.push(body);
      bodyInterface.AddBody(body.jBody.GetID(), this.namespace.EActivation_Activate)
    }

    geometryShape(mesh: three.BufferGeometry) {
      return new GeometryShape(this.engine, mesh)
    }

    body(
      shape: Shape,
      position: three.Vector3Like = new three.Vector3(),
      rotation: three.QuaternionLike = new three.Quaternion()
    ) {
      return new Body(this, shape, position, rotation)
    }
  }

  export interface Shape {
    jShape: JoltNamespace.Shape;

    dispose(): void,
  }

  /**
   * Create a "ConvextHull" shape based on all the position attributes
   * of the provided geometry vertecies.
   * 
   * When you are done, call dipose() on the Shape.
   */
  export class GeometryShape implements Shape {
    engine: Engine;
    jShape: JoltNamespace.Shape;

    constructor(engine: Engine, mesh: three.BufferGeometry) {
      this.engine = engine;
      const { Jolt } = engine;

      const positions = mesh.getAttribute('position');

      if (!positions)
        throw new Error(`Mesh is missing "position" attribute. Cannot build a physics body without those.`);

      let convexHullPoints = new Jolt.ArrayVec3();
      
      for (let i = 0; i < positions.count; i++) {
        let vertex = new Jolt.Vec3(
          positions.getX(i),
          positions.getY(i),
          positions.getZ(i),
        )
        convexHullPoints.push_back(vertex)
      }
      
      let material = new Jolt.PhysicsMaterial();
	    const materialList = new Jolt.PhysicsMaterialList();
      materialList.push_back(material);

      let hullShapeSettings = new Jolt.ConvexHullShapeSettings()
      hullShapeSettings.set_mPoints(convexHullPoints);

      let shapeResult = hullShapeSettings.Create();
      
      if (shapeResult.HasError())
        throw new Error(shapeResult.GetError().c_str())
      
      this.jShape = shapeResult.Get(); 

      this.jShape.AddRef();
      shapeResult.Clear();

      // cleanup
      Jolt.destroy(hullShapeSettings);
    }

    dispose() {
      this.jShape.Release();
    }
  }

  export class Body {
    engine: Engine;
    sim: Simulation;

    jBody: JoltNamespace.Body;
    id: JoltNamespace.BodyID;

    constructor(sim: Simulation, shape: Shape, position: three.Vector3Like, rotatation: three.QuaternionLike) {
      this.sim = sim;
      this.engine = sim.engine;
      const { Jolt } = sim.engine;

      let physics = sim.interface.GetPhysicsSystem();
      let bodies = physics.GetBodyInterface();


      let bodyPosition = new Jolt.RVec3(position.x, position.y, position.z);
      let bodyRotation = new Jolt.Quat(rotatation.x, rotatation.y, rotatation.z, rotatation.w);

      let bodySettings = new Jolt.BodyCreationSettings(
        shape.jShape,
        bodyPosition, bodyRotation,
        Jolt.EMotionType_Dynamic,
        this.sim.objectLayer
      );
      // bodySettings.mOverrideMassProperties = 8;
      
      Jolt.destroy(bodyPosition);
      Jolt.destroy(bodyRotation);

      this.jBody = bodies.CreateBody(bodySettings);
      this.id = this.jBody.GetID();
      Jolt.destroy(bodySettings);
    }

    #position = new three.Vector3();
    get position() {
      const bodyPosition = this.jBody.GetPosition();

      this.#position.set(bodyPosition.GetX(), bodyPosition.GetY(), bodyPosition.GetZ())

      return this.#position;
    }
    #rotation = new three.Quaternion();
    get rotation() {
      const bodyRotation = this.jBody.GetRotation()

      this.#rotation.set(bodyRotation.GetX(), bodyRotation.GetY(), bodyRotation.GetZ(), bodyRotation.GetW())

      return this.#rotation;
    }

    get active() {
      return this.jBody.IsActive()
    }

    impulse(vector: three.Vector3Like) {
      let vectorJ = new this.engine.Jolt.Vec3(vector.x, vector.y, vector.z);

      this.jBody.AddImpulse(vectorJ);
      
      this.engine.Jolt.destroy(vectorJ);
    }
    aImpulse(vector: three.Vector3Like) {
      let vectorJ = new this.engine.Jolt.Vec3(vector.x, vector.y, vector.z);

      this.jBody.AddAngularImpulse(vectorJ);
      
      this.engine.Jolt.destroy(vectorJ);
    }

    remove() {
      this.sim.bodyInterface.RemoveBody(this.id)
    }
  }
}
